import {useTimeUnitTranslation} from '@aboutyou/i18n';
import React, {ComponentProps, FC} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {
  Countdown,
  CountdownUnitProps,
} from '../../molecules/Countdown/Countdown';

interface CouponExpiryDateCountdownProps {
  expiryDate: Date;
  className?: string;
  iconSize?: ComponentProps<typeof Icon>['size'];
  color?: keyof typeof COLORS;
}

export const CountdownChip: FC<CouponExpiryDateCountdownProps> = ({
  expiryDate,
  className,
  iconSize = 'XS',
  color = 'Fill.Black',
}) => {
  return (
    <Container className={className}>
      <Icon
        asset={GenericIconName.Clock}
        size={iconSize}
        fill={COLORS[color]}
      />
      <StyledCountdown
        $color={COLORS[color]}
        endDate={expiryDate}
        unitComponent={UnitComponent}
      />
    </Container>
  );
};

const UnitComponent: React.FC<CountdownUnitProps> = ({value, unit}) => {
  const timeFormat = useTimeUnitTranslation('shorter');

  if (unit === 'days' && Number(value) === 0) {
    return null;
  }

  if (unit === 'hours' && Number(value) === 0) {
    return null;
  }

  return (
    <Unit>
      {value}
      {timeFormat[unit]}
    </Unit>
  );
};

const Unit = styled.div`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.6;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;

  // gap is not working when length of element is overflowing.
  // That is mostly impacting days or larger units
  &:not(:last-child) {
    margin-right: 3px;
  }
`;

const Container = styled.div<{$isHotDeal?: boolean}>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px 4px 10px;
  border-radius: 50px;
  border: 1px solid ${COLORS['Outline.Grey']};
  background-color: ${COLORS['Fill.White']};
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.08);
`;

const StyledCountdown = styled(Countdown)<{$color: string}>`
  border: none;
  color: ${props => props.$color};

  && {
    padding: 0;
  }
`;
